import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader } from 'lucide-react';
import useApi from '../hooks/useApi';
import { useSettings } from '../context/SettingsContext';

const CaseSearchDropdown = ({ onSelect, placeholder = 'Search for a case to link...', includeEfileNotings = true }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { get } = useApi();
  const { settings } = useSettings();
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchCases = async () => {
      if (query.length < 3) {
        setResults([]);
        return;
      }
      
      setLoading(true);
      try {
        const basePromise = get(`/search?q=${query}`);
        const notingPromise =
          includeEfileNotings && isEfileEnabled ? get(`/efile/notings/search?q=${encodeURIComponent(query)}`) : Promise.resolve({ data: [] });
        const [baseResponse, notingResponse] = await Promise.all([basePromise, notingPromise]);

        const baseItems = Array.isArray(baseResponse.data) ? baseResponse.data : [];
        const baseFiltered = baseItems.filter(item => item?.type === 'receipt' || item?.type === 'dispatch');

        const notingItems = Array.isArray(notingResponse.data) ? notingResponse.data : [];
        const notingFiltered = notingItems.filter(item => item?.type === 'efile_noting');

        const merged = [...baseFiltered, ...notingFiltered];
        const deduped = merged.filter((item, idx) => merged.findIndex(x => x.type === item.type && String(x.id) === String(item.id)) === idx);

        setResults(deduped);
        setIsOpen(true);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchCases, 500);
    return () => clearTimeout(debounceTimer);
  }, [query, get]);

  const handleSelect = (item) => {
    onSelect(item);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 3 && setIsOpen(true)}
        />
        {loading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Loader className="h-4 w-4 text-indigo-500 animate-spin" />
          </div>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {results.map((item) => (
            <li
              key={`${item.type}-${item.id}`}
              className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-indigo-600 hover:text-white group border-b border-gray-100 last:border-0"
              onClick={() => handleSelect(item)}
            >
              <div className="flex flex-col">
                <span className="font-medium block truncate">
                  {item.number} - {item.subject}
                </span>
                <span className="text-xs text-gray-500 group-hover:text-indigo-200">
                  {(item.type === 'efile_noting' ? 'NOTING' : item.type.toUpperCase())} | {new Date(item.date).toLocaleDateString()} | {item.status}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      
      {isOpen && query.length >= 3 && results.length === 0 && !loading && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-2 px-3 text-sm text-gray-500 text-center ring-1 ring-black ring-opacity-5">
          No results found
        </div>
      )}
    </div>
  );
};

export default CaseSearchDropdown;
